import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['siretInput', 'hiddenSiret', 'results', 'preview', 'clearButton'];
  static values = {
    url: String,
    context: { type: String, default: 'contractor' },
    debounce: { type: Number, default: 500 },
    existingRecordId: { type: Number, default: 0 },
    initialValue: { type: String, default: '' },
    nameFieldSelector: { type: String, default: '' },
    addressLine1Selector: { type: String, default: '' },
    postalCodeSelector: { type: String, default: '' },
    citySelector: { type: String, default: '' },
  };

  connect() {
    this.timeout = null;
    this.abortController = null;
    this.highlightTimeout = null;
    this.selectedIndex = -1;
    this.selected = false;

    // Edit mode: pre-populate hidden field, format SIRET display
    if (this.siretInputTarget.value) {
      const raw = this.siretInputTarget.value.replace(/\D/g, '');
      if (raw.length === 14) {
        this.hiddenSiretTarget.value = raw;
        this.siretInputTarget.value = this.formatSiret(raw);
      }
    }

    // Close dropdown on outside click
    this.outsideClickHandler = (e) => {
      if (!this.element.contains(e.target)) {
        this.hideResults();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);

    // Listen for typeahead events (sibling controller communication via document)
    this.typeaheadResultsHandler = () => this.hideResults();
    this.typeaheadSelectedHandler = (e) => this.onTypeaheadSelected(e);
    document.addEventListener('sirene-typeahead:results-shown', this.typeaheadResultsHandler);
    document.addEventListener('sirene-typeahead:selected', this.typeaheadSelectedHandler);
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout);
    if (this.highlightTimeout) clearTimeout(this.highlightTimeout);
    if (this.abortController) this.abortController.abort();
    document.removeEventListener('click', this.outsideClickHandler);
    document.removeEventListener('sirene-typeahead:results-shown', this.typeaheadResultsHandler);
    document.removeEventListener('sirene-typeahead:selected', this.typeaheadSelectedHandler);
  }

  // ── SIRET input handlers ──

  onSiretInput() {
    if (this.selected) {
      this.clearSelection();
    }

    const raw = this.siretInputTarget.value;
    const digitsOnly = raw.replace(/\D/g, '');

    // Format and sync hidden field
    this.siretInputTarget.value = this.formatSiret(digitsOnly);
    this.hiddenSiretTarget.value = digitsOnly;

    // Input gate: skip if < 9 digits, search at 9 (SIREN) or 14 (SIRET)
    if (digitsOnly.length < 9) {
      this.hideResults();
      return;
    }
    if (digitsOnly.length !== 9 && digitsOnly.length !== 14) {
      this.hideResults();
      return;
    }

    this.debouncedSearch(digitsOnly);
  }

  onSiretPaste() {
    setTimeout(() => this.onSiretInput(), 0);
  }

  onSiretKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.siretInputTarget.blur();
    }
  }

  // ── Cross-controller event handler: typeahead selected ──

  onTypeaheadSelected(event) {
    const result = event.detail;
    this.selected = true;

    if (result.siret) {
      this.hiddenSiretTarget.value = result.siret;
      this.siretInputTarget.value = this.formatSiret(result.siret);
    }

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove('hidden');
    }

    this.showPreviewCard(result);

    // Auto-fill address only (name already set by typeahead)
    this.autoFillAddressFields(result);
  }

  // ── Shared search logic ──

  debouncedSearch(query) {
    if (this.timeout) clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      this.performSearch(query);
    }, this.debounceValue);
  }

  async performSearch(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.showLoading(query);

    try {
      const params = new URLSearchParams({
        q: query,
        context: this.contextValue,
      });
      if (this.existingRecordIdValue > 0) {
        params.set('exclude_id', this.existingRecordIdValue);
      }

      const response = await fetch(`${this.urlValue}?${params}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();

      if (data.error) {
        this.showError();
        return;
      }

      // Check for duplicates first
      if (data.duplicate) {
        this.hideResults();
        this.showDuplicate(data.duplicate);
        return;
      }

      if (data.results && data.results.length > 0) {
        this.showResults(data.results);
      } else {
        this.hideResults();
        this.showNotFound();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.showError();
    }
  }

  // NOTE: All innerHTML assignments below use only static HTML or values
  // escaped via escapeHtml(). No user-controlled content is inserted raw.

  showLoading(query) {
    const isDigits = /^\d+$/.test(query);
    this.clearPreview();

    const container = this.resultsTarget;
    container.textContent = '';
    const div = document.createElement('div');
    div.className = 'p-4 text-center text-sm text-neutral-500';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'inline-block h-4 w-4 text-primary-600 animate-spin mr-2');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99'
    );
    svg.appendChild(path);
    div.appendChild(svg);
    div.appendChild(
      document.createTextNode(isDigits ? 'Vérification en cours...' : 'Recherche en cours...')
    );
    container.appendChild(div);
    container.classList.remove('hidden');
  }

  showResults(results) {
    this.selectedIndex = -1;
    const container = this.resultsTarget;
    container.textContent = '';

    results.forEach((result, index) => {
      const formattedSiret = result.siret ? this.formatSiret(result.siret) : '';
      const location = [formattedSiret, result.postal_code, result.city]
        .filter(Boolean)
        .join(' \u00b7 ');

      const item = document.createElement('div');
      item.setAttribute('data-result-index', index);
      item.setAttribute('data-action', 'click->sirene-lookup#selectResult');
      item.setAttribute('data-result', JSON.stringify(result));
      item.className =
        'cursor-pointer p-3 hover:bg-primary-50 border-b border-neutral-100 last:border-0 transition-colors';

      const nameDiv = document.createElement('div');
      nameDiv.className = 'text-sm font-medium text-neutral-900';
      nameDiv.textContent = result.company_name || '';
      item.appendChild(nameDiv);

      const locationDiv = document.createElement('div');
      locationDiv.className = 'text-xs text-neutral-500';
      locationDiv.textContent = location;
      item.appendChild(locationDiv);

      container.appendChild(item);
    });

    const footer = document.createElement('div');
    footer.className = 'px-3 py-2 text-xs text-neutral-400 border-t border-neutral-100';
    footer.textContent = `${results.length} résultat${results.length > 1 ? 's' : ''}`;
    container.appendChild(footer);

    container.classList.remove('hidden');

    // Mutual exclusivity: tell typeahead to close its dropdown
    this.dispatch('results-shown', { bubbles: true });
  }

  selectResult(event) {
    const item = event.currentTarget;
    const result = JSON.parse(item.dataset.result);

    this.selected = true;
    this.hideResults();

    // Set SIRET values — visible input shows formatted SIRET (core UX fix)
    if (result.siret) {
      this.hiddenSiretTarget.value = result.siret;
      this.siretInputTarget.value = this.formatSiret(result.siret);
    }

    // Show clear button
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove('hidden');
    }

    // Show preview card
    this.showPreviewCard(result);

    // Auto-fill name + address fields
    this.autoFillFields(result);

    // Dispatch selected event for typeahead coordination
    this.dispatch('selected', { detail: result, bubbles: true });
  }

  showPreviewCard(result) {
    const ceased = result.ceased;
    const borderClass = ceased
      ? 'border-warning-200 bg-warning-50'
      : 'border-success-200 bg-success-50';
    const iconColor = ceased ? 'text-warning-600' : 'text-success-600';
    const statusText = ceased ? 'Entreprise cessée' : 'Entreprise active';
    const formattedSiret = result.siret ? this.formatSiret(result.siret) : '';
    // geo_adresse from Sirene API already includes street + postal code + city
    const address = result.address || '';

    const card = document.createElement('div');
    card.className = `rounded-lg border ${borderClass} p-4 mt-2`;

    // Status header
    const header = document.createElement('div');
    header.className = 'flex items-center gap-2 mb-2';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', `h-5 w-5 ${iconColor}`);
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      ceased
        ? 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
        : 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    );
    svg.appendChild(path);
    header.appendChild(svg);

    const statusSpan = document.createElement('span');
    statusSpan.className = `text-sm font-medium ${ceased ? 'text-warning-800' : 'text-success-800'}`;
    statusSpan.textContent = statusText;
    header.appendChild(statusSpan);
    card.appendChild(header);

    // Details
    const details = document.createElement('div');
    details.className = 'space-y-1 text-sm text-neutral-700';

    const siretRow = document.createElement('div');
    const siretLabel = document.createElement('span');
    siretLabel.className = 'font-medium';
    siretLabel.textContent = 'SIRET';
    siretRow.appendChild(siretLabel);
    siretRow.appendChild(document.createTextNode(' ' + formattedSiret));
    details.appendChild(siretRow);

    if (address) {
      const addrRow = document.createElement('div');
      const addrLabel = document.createElement('span');
      addrLabel.className = 'font-medium';
      addrLabel.textContent = 'Adresse';
      addrRow.appendChild(addrLabel);
      addrRow.appendChild(document.createTextNode(' ' + address));
      details.appendChild(addrRow);
    }

    card.appendChild(details);

    if (ceased) {
      const warning = document.createElement('p');
      warning.className = 'mt-2 text-xs text-warning-700';
      warning.textContent = 'Cette entreprise est indiquée comme cessée.';
      card.appendChild(warning);
    }

    const hint = document.createElement('p');
    hint.className = 'mt-1.5 text-xs text-neutral-500';
    hint.textContent = 'Les champs nom et adresse seront pré-remplis';

    this.previewTarget.textContent = '';
    this.previewTarget.appendChild(card);
    this.previewTarget.appendChild(hint);
    this.previewTarget.classList.remove('hidden');
  }

  showDuplicate(duplicate) {
    const wrapper = document.createElement('div');
    wrapper.className = 'rounded-lg border border-primary-200 bg-primary-50 p-4 mt-2';

    const header = document.createElement('div');
    header.className = 'flex items-center gap-2 mb-1';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'h-5 w-5 text-primary-600');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('d', 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z');
    svg.appendChild(path);
    header.appendChild(svg);

    const title = document.createElement('span');
    title.className = 'text-sm font-medium text-primary-800';
    title.textContent = 'Ce SIRET est déjà utilisé';
    header.appendChild(title);
    wrapper.appendChild(header);

    const body = document.createElement('p');
    body.className = 'text-sm text-primary-700';
    body.appendChild(document.createTextNode('par "'));
    body.appendChild(document.createTextNode(duplicate.name));
    body.appendChild(document.createTextNode('"'));

    // Some contexts (e.g. Intervenant) have no show page → no `path`. Degrade to
    // a name-only duplicate notice rather than emitting `href=null` (#556, R10).
    if (duplicate.path) {
      body.appendChild(document.createTextNode(' '));
      const link = document.createElement('a');
      link.href = duplicate.path;
      link.className = 'ml-1 font-medium text-primary-600 hover:text-primary-500 underline';
      link.textContent = 'Voir la fiche →';
      body.appendChild(link);
    }
    wrapper.appendChild(body);

    this.previewTarget.textContent = '';
    this.previewTarget.appendChild(wrapper);
    this.previewTarget.classList.remove('hidden');
  }

  showNotFound() {
    this.clearPreview();
  }

  showError() {
    this.hideResults();

    const wrapper = document.createElement('div');
    wrapper.className = 'rounded-lg border border-warning-200 bg-warning-50 p-4 mt-2';

    const header = document.createElement('div');
    header.className = 'flex items-center gap-2 mb-1';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'h-5 w-5 text-warning-600');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
    );
    svg.appendChild(path);
    header.appendChild(svg);

    const title = document.createElement('span');
    title.className = 'text-sm font-medium text-warning-800';
    title.textContent = 'Vérification impossible';
    header.appendChild(title);
    wrapper.appendChild(header);

    const msg1 = document.createElement('p');
    msg1.className = 'text-sm text-warning-700';
    msg1.textContent = 'Service temporairement indisponible.';
    wrapper.appendChild(msg1);

    const msg2 = document.createElement('p');
    msg2.className = 'text-sm text-warning-700';
    msg2.textContent = 'Vous pouvez continuer la saisie manuellement.';
    wrapper.appendChild(msg2);

    this.previewTarget.textContent = '';
    this.previewTarget.appendChild(wrapper);
    this.previewTarget.classList.remove('hidden');
  }

  // ── Auto-fill: name + address (used by SIRET selection) ──

  autoFillFields(result) {
    const form = this.element.closest('form');
    if (!form) return;

    const filledFields = [];

    const nameField = this.findField(form, this.nameFieldSelectorValue);
    if (nameField && result.company_name) {
      nameField.value = result.company_name;
      filledFields.push(nameField);
    }

    filledFields.push(...this._fillAddressFields(form, result));

    this.highlightFields(filledFields);
  }

  // ── Auto-fill: address only (used by typeahead selection — name already set) ──

  autoFillAddressFields(result) {
    const form = this.element.closest('form');
    if (!form) return;

    const filledFields = this._fillAddressFields(form, result);
    this.highlightFields(filledFields);
  }

  _fillAddressFields(form, result) {
    const filledFields = [];

    const addressLine1 = this.findField(form, this.addressLine1SelectorValue);
    if (addressLine1 && result.address) {
      addressLine1.value = result.address;
      filledFields.push(addressLine1);
    }

    const postalCode = this.findField(form, this.postalCodeSelectorValue);
    if (postalCode && result.postal_code) {
      postalCode.value = result.postal_code;
      filledFields.push(postalCode);
    }

    const city = this.findField(form, this.citySelectorValue);
    if (city && result.city) {
      city.value = result.city;
      filledFields.push(city);
    }

    return filledFields;
  }

  findField(form, selector) {
    if (!selector) return null;
    return form.querySelector(selector);
  }

  highlightFields(fields) {
    if (this.highlightTimeout) clearTimeout(this.highlightTimeout);

    fields.forEach((field) => {
      field.classList.add('border-success-400', 'bg-success-50', 'border-2');
      field.classList.remove('border-neutral-300');
    });

    this.highlightTimeout = setTimeout(() => {
      fields.forEach((f) => {
        f.classList.remove('border-success-400', 'bg-success-50', 'border-2');
        f.classList.add('border-neutral-300');
      });
    }, 2500);
  }

  clear() {
    this.selected = false;
    this.siretInputTarget.value = '';
    this.hiddenSiretTarget.value = '';
    this.hideResults();
    this.clearPreview();

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.add('hidden');
    }

    // Clear auto-filled fields via form-level selectors
    const form = this.element.closest('form');
    if (form) {
      [
        this.nameFieldSelectorValue,
        this.addressLine1SelectorValue,
        this.postalCodeSelectorValue,
        this.citySelectorValue,
      ]
        .filter(Boolean)
        .forEach((selector) => {
          const field = form.querySelector(selector);
          if (field) field.value = '';
        });
    }

    // Dispatch cleared event for typeahead coordination
    this.dispatch('cleared', { bubbles: true });

    this.siretInputTarget.focus();
  }

  clearSelection() {
    this.selected = false;
    this.hiddenSiretTarget.value = '';
    this.clearPreview();
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.add('hidden');
    }
  }

  clearPreview() {
    this.previewTarget.textContent = '';
    this.previewTarget.classList.add('hidden');
  }

  hideResults() {
    this.resultsTarget.textContent = '';
    this.resultsTarget.classList.add('hidden');
    this.selectedIndex = -1;
  }

  formatSiret(digits) {
    // Format: XXX XXX XXX XXXXX (3-3-3-5)
    const d = digits.replace(/\D/g, '').substring(0, 14);
    const parts = [];
    if (d.length > 0) parts.push(d.substring(0, Math.min(3, d.length)));
    if (d.length > 3) parts.push(d.substring(3, Math.min(6, d.length)));
    if (d.length > 6) parts.push(d.substring(6, Math.min(9, d.length)));
    if (d.length > 9) parts.push(d.substring(9, Math.min(14, d.length)));
    return parts.join(' ');
  }
}
